package top.wcpe.allincore.ui.runtime.event

import top.wcpe.allincore.ui.runtime.element.UIElement
import kotlin.jvm.JvmName
import kotlin.jvm.JvmStatic

/**
 * 全局拖拽会话（Phase 4）：承载"跨元素拖放"的当前状态。
 *
 * 与 [DragCaptureManager] 的 Slider 捕获正交——后者用于单控件内拖动（如 slider 调值），
 * 本类用于把数据从源元素拖到另一个 dropTarget（如物品栏跨槽、列表重排、跨容器拖放）。
 *
 * 单屏单会话：静态字段保存当前会话。生命周期由 [InputDispatcher] 驱动——
 * start 开始、dispatchDragMove 期间更新 currentTarget、dispatchDrop / cancel 结束。
 * 所有访问发生在客户端渲染/输入线程，无跨线程并发。
 */
class DragSession private constructor(
    @get:JvmName("sourceId") val sourceId: String,
    @get:JvmName("sourceElement") val sourceElement: UIElement<*>?,
    @get:JvmName("payload") val payload: Any?,
    mouseX: Double,
    mouseY: Double,
) {
    /** 当前悬停目标；包内由 InputDispatcher 写入。 */
    @get:JvmName("currentTarget")
    var currentTarget: UIElement<*>? = null
        internal set

    @get:JvmName("mouseX")
    var mouseX: Double = mouseX
        internal set

    @get:JvmName("mouseY")
    var mouseY: Double = mouseY
        internal set

    /** 包内：更新当前悬停目标。 */
    fun currentTarget(target: UIElement<*>?) {
        currentTarget = target
    }

    /** 包内：更新鼠标坐标。 */
    fun updateMouse(mx: Double, my: Double) {
        mouseX = mx
        mouseY = my
    }

    /** 中止当前会话（等价于 end，语义上表示未成功放下）。 */
    fun cancel() {
        end()
    }

    companion object {
        private var current: DragSession? = null

        /** 开始一个新会话（无源元素）。 */
        @JvmStatic
        fun start(sourceId: String, payload: Any?, mouseX: Double, mouseY: Double): DragSession =
            start(sourceId, null, payload, mouseX, mouseY)

        /** 以精确源元素开始会话。 */
        @JvmStatic
        fun start(
            sourceId: String,
            sourceElement: UIElement<*>?,
            payload: Any?,
            mouseX: Double,
            mouseY: Double,
        ): DragSession {
            val session = DragSession(sourceId, sourceElement, payload, mouseX, mouseY)
            current = session
            return session
        }

        /** 当前活跃会话；无则返回 null。 */
        @JvmStatic
        fun current(): DragSession? = current

        /** 是否有活跃会话。 */
        @JvmStatic
        fun active(): Boolean = current != null

        /** 结束当前会话（放下完成或中止后调用）。 */
        @JvmStatic
        fun end() {
            current = null
        }
    }
}
